import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './common';

export const ConnectionProviderSchema = z.enum(['meta-cloud', 'gupshup']);
export type ConnectionProvider = z.infer<typeof ConnectionProviderSchema>;

export const ConnectionTierSchema = z.enum(['none', '1', '2', '3', 'oba', 'custom']);
export type ConnectionTier = z.infer<typeof ConnectionTierSchema>;

/**
 * Mapping tier → daily cap. Aplicado em selectGovernedPhoneNumberId via
 * filterAvailablePhones quando connection.dailyCap não tá explicitamente
 * setado (custom).
 *
 * - 'none': null (usa DAILY_CAP_DEFAULT = 200)
 * - '1': 1000  (Tier 1 Meta)
 * - '2': 10000 (Tier 2 Meta)
 * - '3': 100000 (Tier 3 Meta)
 * - 'oba': 0 (sentinel — ilimitado, sem cap)
 * - 'custom': null (caller usa connection.dailyCap)
 */
export const TIER_DAILY_CAP_MAP: Record<ConnectionTier, number | null> = {
  none: null,
  '1': 1000,
  '2': 10000,
  '3': 100000,
  oba: 0,
  custom: null,
};

export const DistributionStrategySchema = z.enum([
  'rotating',
  'round-robin',
  'random',
  'warmup-blocks',
]);
export type DistributionStrategy = z.infer<typeof DistributionStrategySchema>;

/**
 * Saúde do Access Token de uma connection, resultado do último teste contra a
 * Graph API (`POST /connections/:id/test`):
 * - `'unknown'`: nunca testada — a UI não afirma saúde nem alerta.
 * - `'valid'`: último teste passou.
 * - `'invalid'`: último teste falhou (token revogado/expirado etc.) — a UI
 *   mostra um alerta proativo antes de o operador tentar usar e falhar.
 */
export const ConnectionTokenStatusSchema = z.enum(['unknown', 'valid', 'invalid']);
export type ConnectionTokenStatus = z.infer<typeof ConnectionTokenStatusSchema>;

/**
 * Filtros granulares de eventos de webhook que esta connection aceita receber.
 * Cada toggle ausente (undefined) = aceita (default histórico antes desta
 * feature existir). `false` explicitamente = ignora aquele tipo.
 * NULL como objeto inteiro também significa "aceita todos".
 */
export const ConnectionWebhookFiltersSchema = z.object({
  messages: z.boolean().optional(),
  statuses: z.boolean().optional(),
  qualityUpdates: z.boolean().optional(),
  accountAlerts: z.boolean().optional(),
  templateStatus: z.boolean().optional(),
});
export type ConnectionWebhookFilters = z.infer<typeof ConnectionWebhookFiltersSchema>;

export const ConnectionSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  label: z.string().min(1).max(100),
  provider: ConnectionProviderSchema,
  wabaId: z.string().min(1),
  phoneNumberIds: z.array(z.string().min(1)).min(1),
  tier: ConnectionTierSchema.optional(),
  /** Cap diário em mensagens. NULL = default 100. 0 = ilimitado (OBA). */
  dailyCap: z.number().int().nonnegative().nullable().optional(),
  /** Intervalo mínimo entre envios (segundos). NULL = 2s default. */
  sendDelayMinSec: z.number().int().min(0).max(3600).nullable().optional(),
  /** Intervalo máximo entre envios (segundos). NULL = 5s default. */
  sendDelayMaxSec: z.number().int().min(0).max(3600).nullable().optional(),
  tps: z.number().int().positive().optional(),
  distributionStrategy: DistributionStrategySchema,
  isActive: z.boolean(),
  /**
   * Aquecimento automático ligado/desligado. `true` (default) = o engine
   * sobe o volume gradualmente p/ proteger número novo (warmupFactor ativo).
   * `false` = sem ramp-up; o número dispara até o cap × quality. O corte por
   * quality (RED/YELLOW) continua ativo independente desta flag.
   */
  warmupEnabled: z.boolean(),
  accessTokenMasked: z.string(),
  /** Saúde do token no último teste — ver `ConnectionTokenStatusSchema`. */
  tokenStatus: ConnectionTokenStatusSchema,
  /** Quando o token foi testado pela última vez. NULL = nunca testada. */
  lastTokenCheckAt: TimestampSchema.nullable(),
  /** Mensagem humana (PT-BR) do último teste — exibida no tooltip do badge. */
  lastTokenCheckMessage: z.string().nullable(),
  // FK opcional pra BM gerenciada — populada quando connection é criada via
  // dropdown "Selecionar BM" ou linkada via sync (matching de wabaId).
  businessManagerId: UuidSchema.nullable(),
  businessManagerName: z.string().nullable(),
  webhookEventFilters: ConnectionWebhookFiltersSchema.nullable().optional(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Connection = z.infer<typeof ConnectionSchema>;

export const UpdateConnectionWebhookFiltersInputSchema = z.object({
  webhookEventFilters: ConnectionWebhookFiltersSchema.nullable(),
});
export type UpdateConnectionWebhookFiltersInput = z.infer<
  typeof UpdateConnectionWebhookFiltersInputSchema
>;

/**
 * Campos crus da criação de connection, sem a regra cross-field. Mantido
 * separado de `CreateConnectionInputSchema` para que a rota de update possa
 * fazer `.partial()` — `.superRefine` produz um `ZodEffects` que não expõe
 * `.partial()`.
 */
export const CreateConnectionFieldsSchema = z.object({
  label: z.string().min(1).max(100),
  provider: ConnectionProviderSchema,
  wabaId: z.string().min(1),
  phoneNumberIds: z.array(z.string().min(1)).min(1),
  /**
   * Opcional quando `businessManagerId` presente — ver `CreateConnectionInputSchema`.
   * O `preprocess` coage string vazia/só-espaços para `undefined`: um form que
   * esconde o campo de token (modo "herdar da BM") submete `''`, que não deve
   * tropeçar no `min(20)` nem reescrever um token salvo num update.
   */
  accessToken: z.preprocess(
    (v) => (typeof v === 'string' && v.trim().length === 0 ? undefined : v),
    z.string().min(20).optional(),
  ),
  tier: ConnectionTierSchema.optional(),
  dailyCap: z.number().int().nonnegative().nullable().optional(),
  sendDelayMinSec: z.number().int().min(0).max(3600).nullable().optional(),
  sendDelayMaxSec: z.number().int().min(0).max(3600).nullable().optional(),
  tps: z.number().int().positive().optional(),
  distributionStrategy: DistributionStrategySchema.default('rotating'),
  /** Aquecimento automático. Default `true` = warm-up gradual para número novo. */
  warmupEnabled: z.boolean().default(true),
  businessManagerId: UuidSchema.optional(),
});

/**
 * Input de criação de connection.
 *
 * `accessToken` é **opcional quando há `businessManagerId`**: o token vive na
 * BM (`businessManagers.systemUserTokenEncrypted`) e o backend o herda — o
 * operador não redigita um token que a BM já tem. Sem BM (modo manual), o
 * token é obrigatório (`min(20)`). A regra cross-field é validada no
 * `superRefine` abaixo. O token cifrado nunca volta em claro nas respostas da
 * API, então "herdar" significa: o backend copia o token da BM no momento da
 * escrita; a UI não exige redigitação.
 */
export const CreateConnectionInputSchema = CreateConnectionFieldsSchema.superRefine(
  (value, ctx) => {
    // Sem BM: o token é a única fonte de credencial — exige-se um.
    if (!value.businessManagerId && !value.accessToken) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['accessToken'],
        message: 'Informe o Access Token ou selecione uma Business Manager.',
      });
    }
  },
);
export type CreateConnectionInput = z.infer<typeof CreateConnectionInputSchema>;

export const ConnectionsListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ConnectionsListQuery = z.infer<typeof ConnectionsListQuerySchema>;

export const ConnectionsListResponseSchema = z.object({
  connections: z.array(ConnectionSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type ConnectionsListResponse = z.infer<typeof ConnectionsListResponseSchema>;
